import { PROGRAM_STEP_DETAILS, PROGRAM_STEP_DETAILS_MAP } from '@/constants/programStepDetails';
import { getStepsForProgram } from '@/constants/programStepsConfig';

export type CorporateStepStatus = 'completed' | 'in_progress' | 'pending';

export interface CorporateStepEntry {
  id: string;
  stepNumber: string;
  title: string;
  description: string;
  status: CorporateStepStatus;
}

export interface MemberProgramEnrollment {
  program_name?: string | null;
  is_active?: boolean;
  show_intake_call?: string | null;
  program?: { program_id?: string; name?: string } | string | null;
  program_details?: { name?: string | null } | null;
  program_data?: {
    program_steps?: Record<string, string | boolean | null | undefined>;
  } | null;
  user?: string | { user_profile_id?: string } | null;
}

export interface CorporateJourneyEnrollment {
  enrollment_id?: string;
  user?: string | null;
  status?: string;
  activated_at?: string | null;
  current_stage?: string | null;
  assigned_program?: string | null;
  assigned_program_name?: string | null;
  assigned_program_details?: { name?: string | null } | null;
  clinical_enrollment_data?: {
    program?: string | null;
    program_name?: string | null;
    program_progress?: number | null;
    show_intake_call?: string | null;
    program_data?: {
      program_steps?: Record<string, string | boolean | null | undefined>;
    } | null;
  } | null;
}

export interface JourneyStageSnapshot {
  stage: string;
  status?: string;
  is_current?: boolean;
  skipped?: boolean;
}

export interface JourneyProgressSnapshot {
  stages?: JourneyStageSnapshot[];
  completion_percent?: number;
}

const EXCLUDED_STATUSES = new Set(['withdrawn', 'exited']);

const STAGE_STEP_ALIASES: Record<string, string> = {
  blood_urine: 'thyrocare',
  blood_and_urine: 'thyrocare',
  blood_urine_analysis: 'thyrocare',
  book_your_blood_urine_analysis: 'thyrocare',
  questionnaire: 'thrive_check',
  thrive_check_questionnaire: 'thrive_check',
  fill_your_thrive_check_questionnaire: 'thrive_check',
  brain_function_test: 'neurowyzer',
  cognitive: 'neurowyzer',
  in_center_visit: 'in_center',
  schedule_in_center_visit: 'in_center',
  longevity: 'longevity_consultation',
  schedule_longevity_consultation: 'longevity_consultation',
  complete_your_profile: 'profile',
  upload_previous_reports: 'upload_reports',
};

const normalizeToken = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const formatStepLabel = (value: string) =>
  value
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const isThriveProgramName = (programName?: string | null) => {
  if (!programName) return false;
  const name = programName.toLowerCase();
  return (
    name.includes('thrive') ||
    name.includes('pmx') ||
    name.includes('limitless') ||
    name.includes('longevity')
  );
};

const isCompletedStepValue = (rawStatus: string | boolean | null | undefined) =>
  typeof rawStatus === 'string' ? rawStatus.toLowerCase() === 'true' : rawStatus === true;

const isInProgressStepValue = (rawStatus: string | boolean | null | undefined) =>
  typeof rawStatus === 'string' && rawStatus.toLowerCase() === 'in_progress';

const stepIndicatesProgress = (rawStatus: string | boolean | null | undefined) =>
  isCompletedStepValue(rawStatus) || isInProgressStepValue(rawStatus);

export const resolveMemberProgramName = (enrollment?: MemberProgramEnrollment | null) => {
  if (!enrollment) return '';
  if (typeof enrollment.program_name === 'string' && enrollment.program_name.trim()) {
    return enrollment.program_name.trim();
  }
  if (enrollment.program && typeof enrollment.program === 'object' && enrollment.program.name) {
    return enrollment.program.name.trim();
  }
  if (enrollment.program_details?.name?.trim()) {
    return enrollment.program_details.name.trim();
  }
  return '';
};

const memberProgramId = (enrollment: MemberProgramEnrollment) => {
  if (typeof enrollment.program === 'string') return enrollment.program;
  return enrollment.program?.program_id || '';
};

export const groupMemberProgramsByUser = (programs: MemberProgramEnrollment[]) => {
  const grouped: Record<string, MemberProgramEnrollment[]> = {};
  programs.forEach((program) => {
    const rawUser = program.user;
    const userId =
      typeof rawUser === 'string'
        ? rawUser
        : rawUser && typeof rawUser === 'object'
          ? rawUser.user_profile_id || ''
          : '';
    if (!userId) return;
    const existing = grouped[userId] || [];
    existing.push(program);
    grouped[userId] = existing;
    const lower = userId.toLowerCase();
    if (lower !== userId) grouped[lower] = existing;
  });
  return grouped;
};

export const programsForUser = (
  programsByUser: Record<string, MemberProgramEnrollment[]>,
  userId?: string | null
) => {
  if (!userId) return [];
  return programsByUser[userId] || programsByUser[userId.toLowerCase()] || [];
};

export const resolveCorporateProgramName = (
  enrollment: CorporateJourneyEnrollment,
  memberPrograms: MemberProgramEnrollment[] = [],
  programCatalog: Record<string, string> = {}
) => {
  const direct =
    enrollment.assigned_program_details?.name ||
    enrollment.assigned_program_name ||
    enrollment.clinical_enrollment_data?.program_name ||
    '';
  if (direct.trim()) return direct.trim();

  const fromMember = memberPrograms.map(resolveMemberProgramName).find((name) => name);
  if (fromMember) return fromMember;

  const ids = [
    enrollment.assigned_program,
    enrollment.clinical_enrollment_data?.program,
    ...memberPrograms.map(memberProgramId),
  ];
  for (const id of ids) {
    if (id && programCatalog[id]) return programCatalog[id];
  }
  return '';
};

export const journeyShowsProgress = (journey?: JourneyProgressSnapshot | null) => {
  if (!journey) return false;
  if ((journey.completion_percent || 0) > 0) return true;
  const stages = journey.stages || [];
  if (stages.some((stage) => stage.status === 'completed' || stage.skipped)) return true;
  const currentIndex = stages.findIndex((stage) => stage.status === 'current' || stage.is_current);
  return currentIndex > 0;
};

export const enrollmentHasStartedProgram = (
  enrollment: CorporateJourneyEnrollment,
  memberPrograms: MemberProgramEnrollment[] = [],
  journey?: JourneyProgressSnapshot | null
) => {
  if (enrollment.status === 'active' || enrollment.status === 'completed') return true;
  if (enrollment.activated_at) return true;
  if (journeyShowsProgress(journey)) return true;

  const stage = (enrollment.current_stage || '').toLowerCase();
  if (stage && stage !== 'invited' && stage !== 'registered' && stage !== 'not_started') {
    return true;
  }

  const progress = enrollment.clinical_enrollment_data?.program_progress;
  if (typeof progress === 'number' && progress > 0) return true;

  const clinicalSteps = enrollment.clinical_enrollment_data?.program_data?.program_steps;
  if (clinicalSteps && Object.values(clinicalSteps).some(stepIndicatesProgress)) return true;

  return memberPrograms.some((program) => {
    const steps = program.program_data?.program_steps;
    return !!steps && Object.values(steps).some(stepIndicatesProgress);
  });
};

export const deriveCorporateHeadcounts = (
  enrollments: CorporateJourneyEnrollment[],
  programsByUser: Record<string, MemberProgramEnrollment[]> = {},
  journeyByEnrollment: Record<string, JourneyProgressSnapshot | null | undefined> = {}
) => {
  let invited = 0;
  let registered = 0;
  let active = 0;

  enrollments.forEach((enrollment) => {
    if (EXCLUDED_STATUSES.has(enrollment.status || '')) return;
    if (enrollment.status === 'invited') {
      invited += 1;
      return;
    }
    const memberPrograms = programsForUser(programsByUser, enrollment.user);
    const journey = enrollment.enrollment_id
      ? journeyByEnrollment[enrollment.enrollment_id]
      : undefined;
    if (enrollmentHasStartedProgram(enrollment, memberPrograms, journey)) {
      active += 1;
      return;
    }
    if (enrollment.status === 'registered') registered += 1;
  });

  const total = invited + registered + active;
  const registrationRate = total === 0 ? 0 : Math.round(((registered + active) / total) * 100);
  return { invited, registered, active, registrationRate };
};

const pickMemberProgram = (programs: MemberProgramEnrollment[]) => {
  const active = programs.filter((program) => program.is_active !== false);
  const pool = active.length ? active : programs;
  const withSteps = pool.filter((program) => {
    const steps = program.program_data?.program_steps;
    return !!steps && Object.keys(steps).length > 0;
  });
  const thrive = withSteps.find((program) =>
    isThriveProgramName(resolveMemberProgramName(program))
  );
  return thrive || withSteps[0] || pool[0];
};

export const matchJourneyStageToStepId = (stage: string) => {
  const normalized = normalizeToken(stage);
  if (!normalized) return null;
  if (PROGRAM_STEP_DETAILS_MAP[normalized]) return normalized;
  const byTitle = PROGRAM_STEP_DETAILS.find((step) => normalizeToken(step.title) === normalized);
  if (byTitle) return byTitle.id;
  return STAGE_STEP_ALIASES[normalized] || null;
};

const applyJourneyProgress = (
  entries: CorporateStepEntry[],
  journey?: JourneyProgressSnapshot | null
) => {
  if (!journey?.stages?.length) return entries;

  const next = entries.map((entry) => ({ ...entry }));
  const byId = new Map(next.map((entry) => [entry.id, entry]));
  let currentStepId: string | null = null;
  let matched = false;

  journey.stages.forEach((stage) => {
    const stepId = matchJourneyStageToStepId(stage.stage);
    const entry = stepId ? byId.get(stepId) : undefined;
    if (!entry) return;
    matched = true;
    if (stage.status === 'completed' || stage.skipped) {
      entry.status = 'completed';
      return;
    }
    if (stage.status === 'current' || stage.is_current) {
      entry.status = 'in_progress';
      currentStepId = entry.id;
      return;
    }
    if (stage.status === 'pending') entry.status = 'pending';
  });

  if (currentStepId) {
    next.forEach((entry) => {
      if (entry.id !== currentStepId && entry.status === 'in_progress') {
        entry.status = 'pending';
      }
    });
  }

  return matched ? next : entries;
};

export const buildCorporateJourneySteps = (
  enrollment: CorporateJourneyEnrollment,
  memberPrograms: MemberProgramEnrollment[] = [],
  journey?: JourneyProgressSnapshot | null
): CorporateStepEntry[] => {
  const preferred = pickMemberProgram(memberPrograms);
  const programName =
    resolveMemberProgramName(preferred) || resolveCorporateProgramName(enrollment, memberPrograms);
  const programSteps =
    preferred?.program_data?.program_steps ||
    enrollment.clinical_enrollment_data?.program_data?.program_steps ||
    {};
  const showIntakeCall =
    preferred?.show_intake_call === 'true' ||
    enrollment.clinical_enrollment_data?.show_intake_call === 'true' ||
    programSteps.intake_call === 'true' ||
    programSteps.intake_call === true;

  const allowedSteps = getStepsForProgram(programName);
  const firstIncompleteIndex = allowedSteps.findIndex(
    (stepId) => !isCompletedStepValue(programSteps[stepId])
  );

  const entries = allowedSteps
    .filter((stepId) => (stepId === 'intake_call' ? showIntakeCall : true))
    .map((stepId, index) => {
      const detail = PROGRAM_STEP_DETAILS_MAP[stepId];
      const rawStatus = programSteps[stepId];
      let status: CorporateStepStatus = 'pending';
      if (isCompletedStepValue(rawStatus)) status = 'completed';
      else if (isInProgressStepValue(rawStatus)) status = 'in_progress';
      else if (
        firstIncompleteIndex === index ||
        (firstIncompleteIndex === -1 && index === allowedSteps.length - 1)
      ) {
        status = 'in_progress';
      }
      return {
        id: stepId,
        stepNumber: (index + 1).toString().padStart(2, '0'),
        title: detail?.title || formatStepLabel(stepId),
        description: detail?.description || '',
        status,
      };
    });

  return applyJourneyProgress(entries, journey);
};

export const isSafeCorporateReturnPath = (path: string) => {
  if (!path.startsWith('/corporate') || path.startsWith('/corporates')) return false;
  if (path.includes('/profile')) return false;
  return (
    path === '/corporate' ||
    path.startsWith('/corporate?') ||
    path.startsWith('/corporate/employees')
  );
};

export const isCorporateEmployeeProfilePath = (pathname: string) =>
  /^\/corporate\/employees\/[^/]+\/profile\/?$/.test(pathname);

export const corporateAdminProfileReturnPath = (state: unknown) => {
  const from = (state as { from?: unknown } | null)?.from;
  if (typeof from === 'string' && isSafeCorporateReturnPath(from)) return from;
  return '/corporate?tab=employees';
};
